import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { apiService } from '../../../../services/api';
import {
  Empenho,
  EtapaEmpenho,
  SolicitacaoCompra,
  EtapaTransicao,
  FornecedorDadoBancario,
  ParcelaEmpenho,
  EntregaEmpenho,
} from '../../../../types/empenhos';
import { getEtapaLabel as getEtapaLabelUtil, getEtapaBadge as getEtapaBadgeUtil } from '../utils/etapaUtils';
import FormAvancarEtapa from './FormAvancarEtapa';
import FormRetrocederEtapa from './FormRetrocederEtapa';
import FormCancelarEmpenho from './FormCancelarEmpenho';

interface EmpenhoDetalhesProps {
  empenhoId: number | null;
  isOpen: boolean;
  onClose: () => void;
  onUpdate: () => void;
  initialTab?: 'dados' | 'itens' | 'solicitacao' | 'historico' | 'entregas' | 'anexos';
}

// Função para obter label amigável da etapa
const getEtapaLabel = (etapa: EtapaEmpenho): string => {
  return getEtapaLabelUtil(etapa);
};

// Função para obter classe de cor do badge
const getEtapaBadgeClass = (etapa: EtapaEmpenho): string => {
  return `badge ${getEtapaBadgeUtil(etapa)}`;
};

const EmpenhoDetalhes: React.FC<EmpenhoDetalhesProps> = ({
  empenhoId,
  isOpen,
  onClose,
  onUpdate,
  initialTab
}) => {
  const [empenho, setEmpenho] = useState<Empenho | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'dados' | 'itens' | 'solicitacao' | 'historico' | 'entregas' | 'anexos'>('dados');

  // Estados para modais de ações
  const [isFormAvancarOpen, setIsFormAvancarOpen] = useState(false);
  const [isFormRetrocederOpen, setIsFormRetrocederOpen] = useState(false);
  const [isFormCancelarOpen, setIsFormCancelarOpen] = useState(false);

  // Estados para anexos
  const [anexos, setAnexos] = useState<any[]>([]);
  const [isLoadingAnexos, setIsLoadingAnexos] = useState(false);
  const [isAddingAnexo, setIsAddingAnexo] = useState(false);
  const [novoAnexoTipo, setNovoAnexoTipo] = useState('Outro');
  const [novoAnexoTitulo, setNovoAnexoTitulo] = useState('');
  const [novoAnexoDescricao, setNovoAnexoDescricao] = useState('');
  const [novoAnexoArquivo, setNovoAnexoArquivo] = useState<File | null>(null);

  // Estados para entregas
  const [entregas, setEntregas] = useState<EntregaEmpenho[]>([]);
  const [isLoadingEntregas, setIsLoadingEntregas] = useState(false);

  // Carregar detalhes do empenho
  useEffect(() => {
    if (isOpen && empenhoId) {
      carregarEmpenho();
      // Limpar anexos quando mudar de empenho
      setAnexos([]);
      setActiveTab(initialTab || 'dados'); // Resetar para aba inicial ou a especificada
    }
  }, [isOpen, empenhoId, initialTab]);

  const carregarEmpenho = async () => {
    if (!empenhoId) return;

    setIsLoading(true);
    try {
      const response = await apiService.getEmpenhoById(empenhoId);
      if (response.success && response.data) {
        console.log('Empenho detalhado:', response.data);
        setEmpenho(response.data);
      } else {
        toast.error('Erro ao carregar detalhes do empenho');
      }
    } catch (error: any) {
      console.error('Erro ao carregar empenho:', error);
      toast.error(error.message || 'Erro ao carregar empenho');
    } finally {
      setIsLoading(false);
    }
  };

  const formatarValor = (valor: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor);
  };

  const formatarData = (data: string): string => {
    if (!data) return '-';
    try {
      const datePart = data.split('T')[0].split(' ')[0];
      const [year, month, day] = datePart.split('-');
      if (year && month && day) {
        return `${day}/${month}/${year}`;
      }
    } catch (e) {
      // Fallback
    }
    const d = new Date(data);
    return d.toLocaleDateString('pt-BR');
  };

  const handleAvancarEtapa = () => {
    setIsFormAvancarOpen(true);
  };

  const handleRetrocederEtapa = () => {
    setIsFormRetrocederOpen(true);
  };

  const handleCancelar = () => {
    setIsFormCancelarOpen(true);
  };

  const handleAcaoSucesso = () => {
    setIsFormAvancarOpen(false);
    setIsFormRetrocederOpen(false);
    setIsFormCancelarOpen(false);
    carregarEmpenho(); // Recarregar dados atualizados
    onUpdate(); // Notificar componente pai para atualizar lista
  };

  // Carregar anexos quando a aba for ativada
  useEffect(() => {
    if (activeTab === 'anexos' && empenhoId) {
      carregarAnexos();
    }
    if (activeTab === 'entregas' && empenhoId) {
      carregarEntregas();
    }
  }, [activeTab, empenhoId]);

  const carregarAnexos = async () => {
    if (!empenhoId) return;

    setIsLoadingAnexos(true);
    try {
      const response = await apiService.getAnexosEmpenho({
        id_empenho: empenhoId,
        page: 1,
        per_page: 100
      });

      if (response.success && response.data) {
        setAnexos(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar anexos:', error);
      toast.error('Erro ao carregar anexos');
    } finally {
      setIsLoadingAnexos(false);
    }
  };

  const carregarEntregas = async () => {
    if (!empenhoId) return;
    setIsLoadingEntregas(true);
    try {
      const response = await apiService.getEntregasHistorico(empenhoId);
      if (response.success && response.data) {
        setEntregas(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar entregas:', error);
    } finally {
      setIsLoadingEntregas(false);
    }
  };

  const handleAdicionarAnexo = async () => {
    if (!novoAnexoTitulo.trim()) {
      toast.error('Informe o título do anexo');
      return;
    }
    if (!novoAnexoArquivo) {
      toast.error('Selecione um arquivo');
      return;
    }
    if (!empenho) {
      toast.error('Empenho não carregado');
      return;
    }

    try {
      const response = await apiService.createAnexoEmpenho({
        id_empenho: empenhoId!,
        id_contrato: empenho.id_contrato,
        tipo_documento: novoAnexoTipo,
        titulo: novoAnexoTitulo,
        descricao: novoAnexoDescricao,
        arquivos: [novoAnexoArquivo]
      });

      if (response.success) {
        toast.success('Anexo adicionado com sucesso!');
        setNovoAnexoTipo('Outro');
        setNovoAnexoTitulo('');
        setNovoAnexoDescricao('');
        setNovoAnexoArquivo(null);
        setIsAddingAnexo(false);
        carregarAnexos();
      }
    } catch (error) {
      console.error('Erro ao adicionar anexo:', error);
      toast.error('Erro ao adicionar anexo');
    }
  };

  const handleRemoverAnexo = async (idAnexo: number) => {
    if (!window.confirm('Tem certeza que deseja remover este anexo?')) return;

    try {
      const response = await apiService.deleteAnexoEmpenho(idAnexo);

      if (response.success) {
        toast.success('Anexo removido com sucesso!');
        carregarAnexos();
      }
    } catch (error) {
      console.error('Erro ao remover anexo:', error);
      toast.error('Erro ao remover anexo');
    }
  };

  const handleBaixarAnexo = async (anexo: any) => {
    try {
      const arquivos = JSON.parse(anexo.arquivos || '[]');
      if (arquivos.length > 0) {
        const arquivo = arquivos[0];
        await apiService.downloadArquivoAnexoEmpenho(anexo.id, arquivo.nome_salvo);
      }
    } catch (error) {
      console.error('Erro ao baixar anexo:', error);
      toast.error('Erro ao baixar anexo');
    }
  };

  const formatarTamanhoArquivo = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
  };

  // Renderizar ações baseadas na etapa
  const renderAcoes = () => {
    if (!empenho) return null;

    const etapa = empenho.etapa;

    return (
      <div className="d-flex gap-2 mb-3">
        {etapa === 'COMPRAS' && (
          <>
            <button className="btn btn-primary" onClick={handleAvancarEtapa}>
              <i className="ti ti-credit-card me-2" />
              Avançar para Pagamento
            </button>
            <button className="btn btn-outline-danger" onClick={handleCancelar}>
              <i className="ti ti-x me-2" />
              Cancelar
            </button>
          </>
        )}

        {etapa === 'PAGAMENTO' && (
          <>
            <button className="btn btn-primary" onClick={handleAvancarEtapa}>
              <i className="ti ti-check me-2" />
              Confirmar Pagamento
            </button>
            <button className="btn btn-outline-secondary" onClick={handleRetrocederEtapa}>
              <i className="ti ti-arrow-left me-2" />
              Retroceder para Compras
            </button>
            <button className="btn btn-outline-danger" onClick={handleCancelar}>
              <i className="ti ti-x me-2" />
              Cancelar
            </button>
          </>
        )}

        {etapa === 'NF_ENTRADA' && (
          <>
            <button className="btn btn-primary" onClick={handleAvancarEtapa}>
              <i className="ti ti-file-invoice me-2" />
              Informar NF de Entrada
            </button>
            <button className="btn btn-outline-secondary" onClick={handleRetrocederEtapa}>
              <i className="ti ti-arrow-left me-2" />
              Retroceder para Pagamento
            </button>
            <button className="btn btn-outline-danger" onClick={handleCancelar}>
              <i className="ti ti-x me-2" />
              Cancelar
            </button>
          </>
        )}

        {etapa === 'NF_SAIDA' && (
          <>
            <button className="btn btn-primary" onClick={handleAvancarEtapa}>
              <i className="ti ti-file-invoice-dollar me-2" />
              Informar NF de Venda
            </button>
            <button className="btn btn-outline-secondary" onClick={handleRetrocederEtapa}>
              <i className="ti ti-arrow-left me-2" />
              {empenho.atendido_por_estoque == 1 ? 'Retroceder para Compras' : 'Retroceder para NF de Entrada'}
            </button>
            <button className="btn btn-outline-danger" onClick={handleCancelar}>
              <i className="ti ti-x me-2" />
              Cancelar
            </button>
          </>
        )}

        {etapa === 'ENTREGA' && (
          <>
            <button className="btn btn-success" onClick={handleAvancarEtapa}>
              <i className="ti ti-circle-check me-2" />
              Concluir e Gerar CR
            </button>
            <button className="btn btn-outline-secondary" onClick={handleRetrocederEtapa}>
              <i className="ti ti-arrow-left me-2" />
              Retroceder para NF de Saída
            </button>
            <button className="btn btn-outline-danger" onClick={handleCancelar}>
              <i className="ti ti-x me-2" />
              Cancelar
            </button>
          </>
        )}

        {etapa === 'CONCLUIDO' && (
          <>
            <div className="alert alert-success mb-3 w-100">
              <i className="ti ti-check-circle me-2" />
              <strong>Autorização Concluída!</strong> Conta a Receber gerada com sucesso.
            </div>
            <button className="btn btn-outline-secondary" onClick={handleRetrocederEtapa}>
              <i className="ti ti-arrow-left me-2" />
              Retroceder para Entregue
            </button>
          </>
        )}

        {etapa === 'CANCELADO' && (
          <div className="alert alert-danger mb-0">
            <i className="ti ti-x-circle me-2" />
            Esta autorização foi cancelada
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`offcanvas offcanvas-end offcanvas-large ${isOpen ? 'show' : ''}`} style={{ visibility: isOpen ? 'visible' : 'hidden' }}>
      <div className="offcanvas-header">
        <h5 className="offcanvas-title">
          Detalhes da Autorização {empenho?.codigo_empenho || `#${empenhoId}`}
        </h5>
        <button
          type="button"
          className="btn-close"
          onClick={onClose}
        />
      </div>

      <div className="offcanvas-body">
        {isLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Carregando...</span>
            </div>
          </div>
        ) : empenho ? (
          <>
            {/* Ações contextuais */}
            {renderAcoes()}

            {/* Navegação de abas */}
            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'dados' ? 'active' : ''}`}
                  onClick={() => setActiveTab('dados')}
                >
                  Dados Gerais
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'itens' ? 'active' : ''}`}
                  onClick={() => setActiveTab('itens')}
                >
                  Itens da Autorização
                </button>
              </li>
              {['COMPRAS', 'PAGAMENTO', 'NF_ENTRADA', 'NF_SAIDA', 'ENTREGA', 'CONCLUIDO'].includes(empenho.etapa) && (
                <li className="nav-item">
                  <button
                    className={`nav-link ${activeTab === 'solicitacao' ? 'active' : ''}`}
                    onClick={() => setActiveTab('solicitacao')}
                  >
                    Solicitação de Compra
                  </button>
                </li>
              )}
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'historico' ? 'active' : ''}`}
                  onClick={() => setActiveTab('historico')}
                >
                  Histórico
                </button>
              </li>
              {/* Aba Entregas removida do fluxo novo — mantida apenas para histórico de empenhos legados */}
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'anexos' ? 'active' : ''}`}
                  onClick={() => setActiveTab('anexos')}
                >
                  Anexos
                </button>
              </li>
            </ul>

            {/* Conteúdo das abas */}
            <div className="tab-content">
              {/* ABA 1: Dados Gerais */}
              {activeTab === 'dados' && (
                <div className="tab-pane fade show active">
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Código da Autorização</label>
                      <p className="form-control-plaintext">{empenho.codigo_empenho || '-'}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Data da Autorização</label>
                      <p className="form-control-plaintext">{formatarData(empenho.data_empenho)}</p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Contrato</label>
                      <p className="form-control-plaintext">
                        <Link
                          to={`/crm/contratos/detalhes/${empenho.id_contrato}`}
                          className="text-truncate d-inline-block"
                          style={{ maxWidth: '300px' }}
                          title={`Contrato #${empenho.id_contrato}${(empenho as any).contrato_descricao ? ` - ${(empenho as any).contrato_descricao}` : ''}`}
                        >
                          {(empenho as any).contrato_descricao
                            ? (empenho as any).contrato_descricao
                            : `Contrato #${empenho.id_contrato}`}
                        </Link>
                      </p>
                    </div>
                    {empenho.id_empenho_pai && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Autorização Mestre</label>
                        <p className="form-control-plaintext">
                          <Link
                            to={`/crm/contratos/detalhes/${empenho.id_contrato}`}
                            className="text-primary"
                          >
                            <i className="ti ti-link me-1"></i>
                            Autorização pai #{empenho.id_empenho_pai}
                          </Link>
                          <small className="text-muted d-block">Ver no contrato</small>
                        </p>
                      </div>
                    )}
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Valor Total</label>
                      <p className="form-control-plaintext text-primary fw-bold">
                        {formatarValor(empenho.valor_total)}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Etapa Atual</label>
                      <p className="form-control-plaintext">
                        <span className={getEtapaBadgeClass(empenho.etapa)}>
                          {getEtapaLabel(empenho.etapa)}
                        </span>
                        {empenho.atendido_por_estoque == 1 && (
                          <span className="badge bg-success ms-2">
                            <i className="ti ti-package-check me-1" />
                            Atendido por Estoque
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="col-md-6">
                      <label className="form-label fw-bold">Condição de Pagamento</label>
                      <p className="form-control-plaintext">
                        {empenho.condicao_pagamento === 'A_VISTA' ? 'À Vista' :
                         empenho.condicao_pagamento === 'A_PRAZO' ? 'A Prazo' : '-'}
                      </p>
                    </div>
                    {empenho.data_pagamento && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Data do Pagamento</label>
                        <p className="form-control-plaintext">{formatarData(empenho.data_pagamento)}</p>
                      </div>
                    )}
                    {empenho.numero_nf_entrada && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">NF de Entrada</label>
                          <p className="form-control-plaintext">{empenho.numero_nf_entrada}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Data NF Entrada</label>
                          <p className="form-control-plaintext">{formatarData(empenho.data_nf_entrada || '')}</p>
                        </div>
                      </>
                    )}
                    {empenho.numero_nf_venda && (
                      <>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">NF de Venda</label>
                          <p className="form-control-plaintext">{empenho.numero_nf_venda}</p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Data Emissão NF Venda</label>
                          <p className="form-control-plaintext">{formatarData(empenho.data_emissao_nf_venda || '')}</p>
                        </div>
                      </>
                    )}
                    {empenho.id_conta_receber && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Conta a Receber</label>
                        <p className="form-control-plaintext">
                          <span className="badge badge-soft-success">CR #{empenho.id_conta_receber}</span>
                        </p>
                      </div>
                    )}
                    {empenho.id_conta_pagar && (
                      <div className="col-md-6">
                        <label className="form-label fw-bold">Conta a Pagar</label>
                        <p className="form-control-plaintext">
                          <span className="badge badge-soft-warning">CP #{empenho.id_conta_pagar}</span>
                        </p>
                      </div>
                    )}
                    {empenho.descricao && (
                      <div className="col-md-12">
                        <label className="form-label fw-bold">Descrição</label>
                        <p className="form-control-plaintext">{empenho.descricao}</p>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* ABA 2: Itens do Empenho */}
              {activeTab === 'itens' && (
                <div className="tab-pane fade show active">
                  <h6 className="mb-3">Itens da Autorização</h6>
                  <div className="table-responsive">
                    <table className="table table-hover">
                      <thead>
                        <tr>
                          <th>Produto</th>
                          <th>Unidade</th>
                          <th className="text-end">Quantidade</th>
                          <th className="text-end">Valor Unitário</th>
                          <th className="text-end">Valor Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {empenho.itens && empenho.itens.length > 0 ? (
                          empenho.itens.map((item, index) => (
                            <tr key={index}>
                              <td>{item.produto_nome || `Produto #${item.id_produto}`}</td>
                              <td>{item.unidade_medida || '-'}</td>
                              <td className="text-end">{Number(item.quantidade)}</td>
                              <td className="text-end">{formatarValor(item.valor_unitario)}</td>
                              <td className="text-end fw-bold">{formatarValor(item.valor_total || 0)}</td>
                            </tr>
                          ))
                        ) : (
                          <tr>
                            <td colSpan={5} className="text-center text-muted">
                              Nenhum item encontrado
                            </td>
                          </tr>
                        )}
                      </tbody>
                      {empenho.itens && empenho.itens.length > 0 && (
                        <tfoot>
                          <tr className="table-active">
                            <td colSpan={4} className="text-end fw-bold">Total:</td>
                            <td className="text-end fw-bold">{formatarValor(empenho.valor_total)}</td>
                          </tr>
                        </tfoot>
                      )}
                    </table>
                  </div>
                </div>
              )}

              {/* ABA 3: Solicitação de Compra */}
              {activeTab === 'solicitacao' && (
                <div className="tab-pane fade show active">
                  <h6 className="mb-3">Solicitação de Compra</h6>
                  {empenho.atendido_por_estoque == 1 ? (
                    <div className="alert alert-info">
                      <i className="ti ti-info-circle me-2" />
                      Esta autorização foi atendida por estoque existente. Não foi gerada solicitação de compra.
                    </div>
                  ) : empenho.solicitacao_compra ? (
                    <div>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Fornecedor</label>
                          <p className="form-control-plaintext">
                            {empenho.solicitacao_compra.fornecedor_nome || `Fornecedor #${empenho.solicitacao_compra.id_fornecedor}`}
                          </p>
                        </div>
                        <div className="col-md-6">
                          <label className="form-label fw-bold">Situação</label>
                          <p className="form-control-plaintext">
                            <span className={`badge ${
                              empenho.solicitacao_compra.situacao === 'PENDENTE' ? 'badge-soft-warning' :
                              empenho.solicitacao_compra.situacao === 'APROVADA' ? 'badge-soft-success' :
                              empenho.solicitacao_compra.situacao === 'CANCELADA' ? 'badge-soft-danger' :
                              'badge-soft-secondary'
                            }`}>
                              {empenho.solicitacao_compra.situacao}
                            </span>
                          </p>
                        </div>
                      </div>

                      <div className="table-responsive">
                        <table className="table table-hover">
                          <thead>
                            <tr>
                              <th>Produto</th>
                              <th className="text-end">Qtd Autorização</th>
                              <th className="text-end">Qtd Compra</th>
                              <th className="text-end">Diferença</th>
                              <th className="text-end">Custo Unitário</th>
                              <th className="text-end">Custo Total</th>
                            </tr>
                          </thead>
                          <tbody>
                            {empenho.solicitacao_compra.itens?.map((item, index) => {
                              const dif = item.diferenca_quantidade;
                              return (
                                <tr key={index}>
                                  <td>{item.produto_nome || `Produto #${item.id_produto}`}</td>
                                  <td className="text-end">{item.quantidade_empenho != null ? Number(item.quantidade_empenho) : '-'}</td>
                                  <td className="text-end">{Number(item.quantidade)}</td>
                                  <td className="text-end">
                                    {dif != null ? (
                                      <span className={dif === 0 ? 'text-success' : dif < 0 ? 'text-warning fw-bold' : 'text-info fw-bold'}>
                                        {dif > 0 ? '+' : ''}{Number(dif)}
                                      </span>
                                    ) : '-'}
                                  </td>
                                  <td className="text-end">
                                    {item.valor_custo_unitario ? formatarValor(item.valor_custo_unitario) : '-'}
                                  </td>
                                  <td className="text-end fw-bold">
                                    {formatarValor(item.valor_total || 0)}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="table-active">
                              <td colSpan={5} className="text-end fw-bold">Total:</td>
                              <td className="text-end fw-bold">
                                {formatarValor(empenho.solicitacao_compra.valor_total_custo)}
                              </td>
                            </tr>
                          </tfoot>
                        </table>
                      </div>

                      {/* Seção de Dados Bancários */}
                      {['PAGAMENTO', 'NF_ENTRADA', 'NF_SAIDA', 'ENTREGA', 'CONCLUIDO'].includes(empenho.etapa) && empenho.solicitacao_compra.dados_bancarios && (
                        <div className="card mt-4">
                          <div className="card-header">
                            <h6 className="mb-0">
                              <i className="bi bi-bank me-2"></i>
                              Dados Bancários para Pagamento
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row">
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Banco</label>
                                <p className="form-control-plaintext">
                                  {empenho.solicitacao_compra.dados_bancarios.banco}
                                </p>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Tipo</label>
                                <p className="form-control-plaintext">
                                  {empenho.solicitacao_compra.dados_bancarios.tipo === 'CONTA_CORRENTE' ? 'Conta Corrente' :
                                   empenho.solicitacao_compra.dados_bancarios.tipo === 'CONTA_POUPANCA' ? 'Conta Poupança' : 'PIX'}
                                </p>
                              </div>
                              {empenho.solicitacao_compra.dados_bancarios.tipo !== 'PIX' && (
                                <>
                                  <div className="col-md-4">
                                    <label className="form-label fw-bold">Agência</label>
                                    <p className="form-control-plaintext">
                                      {empenho.solicitacao_compra.dados_bancarios.agencia}
                                      {empenho.solicitacao_compra.dados_bancarios.agencia_digito && ` - ${empenho.solicitacao_compra.dados_bancarios.agencia_digito}`}
                                    </p>
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label fw-bold">Conta</label>
                                    <p className="form-control-plaintext">
                                      {empenho.solicitacao_compra.dados_bancarios.conta}
                                      {empenho.solicitacao_compra.dados_bancarios.conta_digito && ` - ${empenho.solicitacao_compra.dados_bancarios.conta_digito}`}
                                    </p>
                                  </div>
                                  <div className="col-md-4">
                                    <label className="form-label fw-bold">Titular</label>
                                    <p className="form-control-plaintext">{empenho.solicitacao_compra.dados_bancarios.titular}</p>
                                  </div>
                                </>
                              )}
                              {empenho.solicitacao_compra.dados_bancarios.tipo === 'PIX' && (
                                <>
                                  <div className="col-md-6">
                                    <label className="form-label fw-bold">Tipo de Chave PIX</label>
                                    <p className="form-control-plaintext">{empenho.solicitacao_compra.dados_bancarios.pix_tipo_chave}</p>
                                  </div>
                                  <div className="col-md-6">
                                    <label className="form-label fw-bold">Chave PIX</label>
                                    <p className="form-control-plaintext">{empenho.solicitacao_compra.dados_bancarios.pix_chave}</p>
                                  </div>
                                  <div className="col-md-6">
                                    <label className="form-label fw-bold">Titular</label>
                                    <p className="form-control-plaintext">{empenho.solicitacao_compra.dados_bancarios.titular}</p>
                                  </div>
                                </>
                              )}
                            </div>
                            {empenho.solicitacao_compra.dados_bancarios.observacoes && (
                              <div className="row mt-3">
                                <div className="col-12">
                                  <label className="form-label fw-bold">Observações</label>
                                  <p className="form-control-plaintext">{empenho.solicitacao_compra.dados_bancarios.observacoes}</p>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Seção de Parcelas / Contas a Pagar */}
                      {['PAGAMENTO', 'NF_ENTRADA', 'NF_SAIDA', 'ENTREGA', 'CONCLUIDO'].includes(empenho.etapa) && empenho.solicitacao_compra.parcelas && empenho.solicitacao_compra.parcelas.length > 0 && (
                        <div className="card mt-4">
                          <div className="card-header">
                            <h6 className="mb-0">
                              <i className="bi bi-calendar-range me-2"></i>
                              Parcelas do Pagamento
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="table-responsive">
                              <table className="table table-hover">
                                <thead>
                                  <tr>
                                    <th>Nº</th>
                                    <th>Vencimento</th>
                                    <th className="text-end">Valor</th>
                                    <th className="text-end">Status</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {empenho.solicitacao_compra.parcelas.map((parcela, index) => (
                                    <tr key={index}>
                                      <td>{parcela.numero_parcela || index + 1}</td>
                                      <td>{formatarData(parcela.data_vencimento)}</td>
                                      <td className="text-end fw-bold">{formatarValor(parcela.valor)}</td>
                                      <td className="text-end">
                                        <span className={`badge ${
                                          parcela.situacao === 'PENDENTE' ? 'badge-soft-warning' :
                                          parcela.situacao === 'GERADA' ? 'badge-soft-info' :
                                          parcela.situacao === 'PAGA' ? 'badge-soft-success' :
                                          'badge-soft-secondary'
                                        }`}>
                                          {parcela.situacao}
                                        </span>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                                <tfoot>
                                  <tr className="table-active">
                                    <td colSpan={3} className="text-end fw-bold">Total:</td>
                                    <td className="text-end fw-bold">{formatarValor(empenho.solicitacao_compra.valor_total_custo)}</td>
                                  </tr>
                                </tfoot>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* NF de Entrada (NF_ENTRADA em diante) */}
                      {['NF_ENTRADA', 'NF_SAIDA', 'ENTREGA', 'CONCLUIDO'].includes(empenho.etapa) && empenho.numero_nf_entrada && (
                        <div className="card mt-4">
                          <div className="card-header">
                            <h6 className="mb-0">
                              <i className="ti ti-file-invoice me-2" />
                              Nota Fiscal de Entrada (Fornecedor)
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Número da NF</label>
                                <p className="form-control-plaintext">{empenho.numero_nf_entrada}</p>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Data da NF</label>
                                <p className="form-control-plaintext">{formatarData(empenho.data_nf_entrada || '')}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* NF de Venda (NF_SAIDA em diante) */}
                      {['NF_SAIDA', 'ENTREGA', 'CONCLUIDO'].includes(empenho.etapa) && empenho.numero_nf_venda && (
                        <div className="card mt-4">
                          <div className="card-header">
                            <h6 className="mb-0">
                              <i className="ti ti-file-invoice-dollar me-2" />
                              Nota Fiscal de Venda (Cliente)
                            </h6>
                          </div>
                          <div className="card-body">
                            <div className="row g-3">
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Número da NF</label>
                                <p className="form-control-plaintext">{empenho.numero_nf_venda}</p>
                              </div>
                              <div className="col-md-6">
                                <label className="form-label fw-bold">Data de Emissão</label>
                                <p className="form-control-plaintext">{formatarData(empenho.data_emissao_nf_venda || '')}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Conta a Receber (CONCLUIDO) */}
                      {empenho.etapa === 'CONCLUIDO' && empenho.id_conta_receber && (
                        <div className="card mt-4 border-success">
                          <div className="card-header bg-success text-white">
                            <h6 className="mb-0">
                              <i className="ti ti-receipt me-2" />
                              Conta a Receber Gerada
                            </h6>
                          </div>
                          <div className="card-body">
                            <span className="badge badge-soft-success fs-6">CR #{empenho.id_conta_receber}</span>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      Solicitação de compra ainda não foi gerada
                    </div>
                  )}
                </div>
              )}

              {/* ABA 4: Histórico */}
              {activeTab === 'historico' && (
                <div className="tab-pane fade show active">
                  <h6 className="mb-3">Histórico de Etapas</h6>
                  {empenho.historico_etapas && empenho.historico_etapas.length > 0 ? (
                    <div className="timeline">
                      {empenho.historico_etapas.map((transicao, index) => (
                        <div key={index} className="timeline-item mb-3">
                          <div className="d-flex">
                            <div className="flex-shrink-0">
                              <span className={`badge ${
                                transicao.tipo_transicao === 'AVANCO' ? 'badge-soft-success' :
                                transicao.tipo_transicao === 'RETROCESSO' ? 'badge-soft-warning' :
                                transicao.tipo_transicao === 'PULO_ESTOQUE' ? 'badge-soft-info' :
                                'badge-soft-danger'
                              }`}>
                                {transicao.tipo_transicao}
                              </span>
                            </div>
                            <div className="flex-grow-1 ms-3">
                              <h6 className="mb-1">
                                {transicao.etapa_anterior ? getEtapaLabel(transicao.etapa_anterior) : 'Início'} → {getEtapaLabel(transicao.etapa_nova)}
                              </h6>
                              <p className="text-muted mb-1">
                                <small>
                                  {formatarData(transicao.data_criacao)} por {transicao.usuario_nome || `Usuário #${transicao.criado_por}`}
                                </small>
                              </p>
                              {transicao.observacoes && (
                                <p className="mb-0">
                                  <small>{transicao.observacoes}</small>
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-5 text-muted">
                      Nenhuma transição registrada
                    </div>
                  )}
                </div>
              )}

              {/* ABA 5: Entregas */}
              {activeTab === 'entregas' && (
                <div className="tab-pane fade show active">
                  <h6 className="mb-3">Histórico de Entregas</h6>
                  {isLoadingEntregas ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status" />
                    </div>
                  ) : entregas.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ti ti-package-off" style={{ fontSize: '48px' }} />
                      <p className="mt-2">Nenhuma entrega registrada ainda</p>
                    </div>
                  ) : (
                    <div className="timeline">
                      {entregas.map((entrega, index) => (
                        <div key={entrega.id} className="card mb-3">
                          <div className="card-header d-flex justify-content-between align-items-center py-2">
                            <span>
                              <strong>Entrega #{index + 1}</strong>
                              {entrega.numero_nf && <span className="ms-2 text-muted">— NF: {entrega.numero_nf}</span>}
                            </span>
                            <span className={`badge ${
                              entrega.status === 'CONCLUIDA' ? 'badge-soft-success' :
                              entrega.status === 'CANCELADA' ? 'badge-soft-danger' :
                              'badge-soft-warning'
                            }`}>
                              {entrega.status === 'CONCLUIDA' ? 'Concluída' :
                               entrega.status === 'CANCELADA' ? 'Cancelada' : 'Aguardando Romaneio'}
                            </span>
                          </div>
                          <div className="card-body">
                            <div className="row g-2 mb-2">
                              <div className="col-md-4">
                                <small className="text-muted d-block">Data da Entrega</small>
                                <strong>{entrega.data_entrega
                                  ? entrega.data_entrega.split('-').reverse().join('/')
                                  : '-'}
                                </strong>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted d-block">Valor da Remessa</small>
                                <strong className="text-primary">
                                  {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(entrega.valor_entrega)}
                                </strong>
                              </div>
                              <div className="col-md-4">
                                <small className="text-muted d-block">Vencimento CR</small>
                                <strong>{entrega.data_vencimento_cr
                                  ? entrega.data_vencimento_cr.split('-').reverse().join('/')
                                  : '-'}
                                </strong>
                              </div>
                            </div>
                            {entrega.id_conta_receber && (
                              <div className="mt-2">
                                <small className="text-muted">Conta a Receber: </small>
                                <span className="badge badge-soft-info">CR #{entrega.id_conta_receber}</span>
                              </div>
                            )}
                            {entrega.observacoes && (
                              <div className="mt-2">
                                <small className="text-muted d-block">Observações</small>
                                <small>{entrega.observacoes}</small>
                              </div>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* ABA 6: Anexos */}
              {activeTab === 'anexos' && (
                <div className="tab-pane fade show active">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0">Anexos da Autorização</h6>
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => setIsAddingAnexo(!isAddingAnexo)}
                    >
                      <i className="ti ti-plus me-2"></i>
                      Adicionar Anexo
                    </button>
                  </div>

                  {/* Formulário de adicionar anexo */}
                  {isAddingAnexo && (
                    <div className="card mb-3 border-primary">
                      <div className="card-body">
                        <h6 className="card-title mb-3">Novo Anexo</h6>
                        <div className="row g-3">
                          <div className="col-md-6">
                            <label className="form-label">Tipo de Documento *</label>
                            <select
                              className="form-select"
                              value={novoAnexoTipo}
                              onChange={(e) => setNovoAnexoTipo(e.target.value)}
                            >
                              <option value="Nota Fiscal de Saída">Nota Fiscal de Saída</option>
                              <option value="Romaneio">Romaneio</option>
                              <option value="Comprovante de Pagamento">Comprovante de Pagamento</option>
                              <option value="Nota Fiscal de Entrada">Nota Fiscal de Entrada</option>
                              <option value="Documento da Autorização">Documento da Autorização</option>
                              <option value="Outro">Outro</option>
                            </select>
                          </div>
                          <div className="col-md-6">
                            <label className="form-label">Título *</label>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Ex: NF 12345"
                              value={novoAnexoTitulo}
                              onChange={(e) => setNovoAnexoTitulo(e.target.value)}
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Descrição</label>
                            <textarea
                              className="form-control"
                              rows={2}
                              placeholder="Descrição opcional..."
                              value={novoAnexoDescricao}
                              onChange={(e) => setNovoAnexoDescricao(e.target.value)}
                            />
                          </div>
                          <div className="col-md-12">
                            <label className="form-label">Arquivo *</label>
                            <input
                              type="file"
                              className="form-control"
                              accept="application/pdf,image/jpeg,image/jpg,image/png"
                              onChange={(e) => setNovoAnexoArquivo(e.target.files?.[0] || null)}
                            />
                            <small className="text-muted">Formatos aceitos: PDF, JPG, PNG</small>
                          </div>
                        </div>
                        <div className="mt-3 d-flex gap-2">
                          <button
                            className="btn btn-primary"
                            onClick={handleAdicionarAnexo}
                          >
                            <i className="ti ti-check me-2"></i>
                            Salvar Anexo
                          </button>
                          <button
                            className="btn btn-secondary"
                            onClick={() => {
                              setIsAddingAnexo(false);
                              setNovoAnexoTipo('Outro');
                              setNovoAnexoTitulo('');
                              setNovoAnexoDescricao('');
                              setNovoAnexoArquivo(null);
                            }}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Lista de anexos */}
                  {isLoadingAnexos ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Carregando...</span>
                      </div>
                    </div>
                  ) : anexos.length === 0 ? (
                    <div className="text-center py-5 text-muted">
                      <i className="ti ti-paperclip" style={{ fontSize: '48px' }} />
                      <p className="mt-2">Nenhum anexo adicionado ainda</p>
                    </div>
                  ) : (
                    <div className="list-group">
                      {anexos.map((anexo) => {
                        const arquivos = JSON.parse(anexo.arquivos || '[]');
                        const arquivo = arquivos[0] || {};

                        return (
                          <div key={anexo.id} className="list-group-item">
                            <div className="d-flex align-items-start gap-3">
                              <div className="flex-shrink-0">
                                <i
                                  className={`ti ${
                                    arquivo.tipo?.includes('pdf')
                                      ? 'ti-file-type-pdf text-danger'
                                      : 'ti-file-type-jpg text-primary'
                                  }`}
                                  style={{ fontSize: '32px' }}
                                />
                              </div>
                              <div className="flex-grow-1">
                                <div className="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 className="mb-1">{anexo.titulo}</h6>
                                    <p className="text-muted small mb-1">{anexo.descricao || 'Sem descrição'}</p>
                                    <div className="d-flex gap-3 small text-muted">
                                      <span>
                                        <i className="ti ti-tag me-1"></i>
                                        {anexo.tipo_documento}
                                      </span>
                                      <span>
                                        <i className="ti ti-file me-1"></i>
                                        {arquivo.nome_original}
                                      </span>
                                      <span>
                                        <i className="ti ti-database me-1"></i>
                                        {formatarTamanhoArquivo(arquivo.tamanho || 0)}
                                      </span>
                                      <span>
                                        <i className="ti ti-calendar me-1"></i>
                                        {formatarData(anexo.data_upload)}
                                      </span>
                                    </div>
                                  </div>
                                  <div className="d-flex gap-2">
                                    <button
                                      className="btn btn-sm btn-outline-primary"
                                      onClick={() => handleBaixarAnexo(anexo)}
                                      title="Baixar"
                                    >
                                      <i className="ti-download"></i>
                                    </button>
                                    <button
                                      className="btn btn-sm btn-outline-danger"
                                      onClick={() => handleRemoverAnexo(anexo.id)}
                                      title="Remover"
                                    >
                                      <i className="ti ti-trash"></i>
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </>
        ) : (
          <div className="text-center py-5">
            <i className="ti ti-alert-circle" style={{ fontSize: '48px', color: '#ccc' }} />
            <p className="text-muted mt-2">Empenho não encontrado</p>
          </div>
        )}
      </div>

      {/* Modais de Ações */}
      {empenho && (
        <>
          <FormAvancarEtapa
            empenho={empenho}
            isOpen={isFormAvancarOpen}
            onClose={() => setIsFormAvancarOpen(false)}
            onSuccess={handleAcaoSucesso}
          />
          <FormRetrocederEtapa
            empenho={empenho}
            isOpen={isFormRetrocederOpen}
            onClose={() => setIsFormRetrocederOpen(false)}
            onSuccess={handleAcaoSucesso}
          />
          <FormCancelarEmpenho
            empenho={empenho}
            isOpen={isFormCancelarOpen}
            onClose={() => setIsFormCancelarOpen(false)}
            onSuccess={handleAcaoSucesso}
          />
        </>
      )}
    </div>
  );
};

export default EmpenhoDetalhes;
